import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { MeetingPlan, MeetingPlanLine } from '../api/types.js';
import { useMeetingPlanActions } from '../hooks/use-meetings.js';
import { Button } from './button.js';
import { PlainText } from './plain-text.js';

// A routed meeting's plan (ADR-037 step 3): what the board's sprint session proposes to change in Jira after the
// meeting, the operator keeping or leaving each line; Apply has the API write the kept ones. Nothing is written before.
// A planning gives no such plan: its record (the priorities, who takes what, the estimates) is read by the next
// Prepare the next sprint; the operator only leaves it out.

const day = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const TITLE: Record<MeetingPlan['task'], string> = {
  cab: 'What the CAB changes in Jira',
  daily: 'What the stand-up changes in the sprint',
  planning: 'What the planning agreed for the next sprint',
};
const WAITING: Partial<Record<MeetingPlan['status'], string>> = {
  queued: 'The sprint session is reading the notes; what it finds shows here.',
  applying: 'Writing to Jira…',
};

/** What a line changes, in words: one short phrase per field */
function changes(l: MeetingPlanLine): string[] {
  return [
    l.status ? `status to ${l.status}` : '',
    l.assignee ? `assigned to ${l.assignee}` : '',
    l.dueDate ? `due ${day.format(new Date(`${l.dueDate}T00:00:00`))}` : '',
    l.sprint ? `in ${l.sprint}` : '',
    l.window ? `on ${weekday.format(new Date(`${l.window.day}T00:00:00Z`))}, ${l.window.start}–${l.window.end}` : '',
    l.storyPoints !== undefined ? `${l.storyPoints} SP` : '',
    l.comment ? 'a comment' : '',
  ].filter(Boolean);
}

/** What became of a line's invitation; a proposal waits on the server page, where Calendar changes are decided */
function Invitation({ serverId, said }: { serverId: string; said: string }) {
  const failed = said.startsWith('No invitation');
  const at = said.indexOf('Calendar changes');
  return (
    <p className="text-xs" style={{ color: failed ? 'var(--color-warn)' : 'var(--color-text-2)' }}>
      {at < 0 ? said : <>{said.slice(0, at)}<Link to={`/servers/${serverId}#calendar-title`} className="underline underline-offset-2 hover:no-underline" style={{ color: 'var(--color-brand-light)' }}>Calendar changes</Link>{said.slice(at + 'Calendar changes'.length)}</>}
    </p>
  );
}

export function MeetingPlanBlock({ serverId, eventId, plan, isAdmin }: { serverId: string; eventId: string; plan: MeetingPlan; isAdmin: boolean }) {
  const { apply, dismiss } = useMeetingPlanActions(serverId);
  const lines = plan.lines ?? [];
  const [kept, setKept] = useState<Set<string>>(() => new Set(lines.map((l) => l.key)));
  const result = new Map((plan.results ?? []).map((r) => [r.key, r]));
  const decidable = (plan.status === 'pending' || plan.status === 'failed') && lines.length > 0;
  const muted = { color: 'var(--color-text-3)' };
  const toggle = (key: string) => setKept((k) => { const n = new Set(k); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const title = TITLE[plan.task] ?? 'What the meeting changes in Jira';
  const recorded = plan.status === 'recorded';

  if (plan.status === 'dismissed') return <p className="mt-2 text-xs" style={muted}>{plan.task === 'planning' ? 'Left out of the next sprint\'s plan' : 'Plan dismissed'}{plan.decidedBy ? ` by ${plan.decidedBy}` : ''}: nothing written.</p>;
  if (WAITING[plan.status]) return <p className="mt-2 text-xs" style={muted} aria-live="polite">{WAITING[plan.status]}</p>;
  if (plan.status === 'held') return <p className="mt-2 text-xs" style={{ color: 'var(--color-warn)' }}>{plan.error ?? 'The notes were held by the screening: nothing was sent to the sprint session.'}</p>;

  return (
    <section className="mt-3 rounded-md border p-3" style={{ borderColor: 'var(--color-border)' }} aria-label={title}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>{title}</h4>
        {plan.runSessionId && <Link to={`/servers/${serverId}/sessions/${plan.runSessionId}`} className="text-xs hover:underline" style={{ color: 'var(--color-brand-light)' }}>The sprint session</Link>}
      </div>
      {plan.notes && <PlainText text={plan.notes} className="mt-1 text-sm" />}
      {(plan.priorities?.length ?? 0) > 0 && (
        <ul className="mt-2 space-y-1 border-l-2 pl-3 text-sm" style={{ borderColor: 'var(--color-brand)', color: 'var(--color-text-2)' }} aria-label="Priorities">
          {plan.priorities!.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      )}
      {(plan.flags?.length ?? 0) > 0 && (
        <ul className="mt-2 space-y-1 text-xs" style={{ color: 'var(--color-warn)' }} aria-label="To look at">
          {plan.flags!.map((f, i) => <li key={i}>{f}</li>)}
        </ul>
      )}
      {lines.length === 0 ? (
        <p className="mt-2 text-xs" style={muted}>{recorded ? 'No ticket placed by the meeting.' : 'Jira matches the meeting: nothing to change.'}</p>
      ) : (
        <ul className="mt-2">
          {lines.map((l) => {
            const r = result.get(l.key);
            return (
              <li key={l.key} className="flex gap-2 border-t py-2 first:border-t-0" style={{ borderColor: 'var(--color-border)' }}>
                {decidable && isAdmin && (
                  <input type="checkbox" className="mt-1 h-4 w-4 flex-shrink-0" checked={kept.has(l.key)} onChange={() => toggle(l.key)} aria-label={`Keep ${l.key}`} />
                )}
                <div className="min-w-0 text-sm">
                  <a href={`https://${plan.site}.atlassian.net/browse/${l.key}`} target="_blank" rel="noreferrer" className="font-medium hover:underline" style={{ color: 'var(--color-text)' }}>{l.key}</a>
                  <span style={{ color: 'var(--color-text-2)' }}>: {changes(l).join(', ')}</span>
                  {l.reason && <p className="text-xs" style={muted}>{l.reason}</p>}
                  {l.comment && <p className="text-xs" style={muted}>Comment: {l.comment}</p>}
                  {r && <p className="text-xs" style={{ color: r.ok ? 'var(--color-success, var(--color-text-2))' : 'var(--color-error)' }}>{r.ok ? 'Written.' : `Not written: ${r.error}`}</p>}
                  {r?.invitation && <Invitation serverId={serverId} said={r.invitation} />}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {recorded && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
          <p className="text-xs" style={muted}>The next Prepare the next sprint starts from this. Nothing is written to Jira from here.</p>
          {isAdmin && <Button size="sm" variant="ghost" className="max-lg:min-h-10" disabled={dismiss.isPending} onClick={() => dismiss.mutate({ eventId })}>Leave it out</Button>}
        </div>
      )}
      {plan.status === 'applied' && <p className="mt-2 text-xs" style={muted}>Applied{plan.decidedBy ? ` by ${plan.decidedBy}` : ''}.</p>}
      {plan.status === 'failed' && plan.error && <p role="alert" className="mt-2 text-xs" style={{ color: 'var(--color-error)' }}>{plan.error}</p>}
      {decidable && isAdmin && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button size="sm" className="max-lg:min-h-10" disabled={!kept.size || apply.isPending} onClick={() => apply.mutate({ eventId, keys: lines.filter((l) => kept.has(l.key)).map((l) => l.key) })}>
            {apply.isPending ? 'Writing to Jira…' : plan.status === 'failed' ? `Apply again (${kept.size})` : `Apply the ${kept.size} kept`}
          </Button>
          <Button size="sm" variant="ghost" className="max-lg:min-h-10" disabled={dismiss.isPending} onClick={() => dismiss.mutate({ eventId })}>Dismiss</Button>
        </div>
      )}
      {(apply.error || dismiss.error) && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>{((apply.error ?? dismiss.error) as Error).message}</p>}
    </section>
  );
}
